import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { toastSuccess, toastError, toastInfo } from "../../utils/alertHelper";
import { v4 as uuidv4 } from "uuid";

function Login() {
  const [locationAllowed, setLocationAllowed] = useState(null); // null = not checked, true/false after check
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [coords, setCoords] = useState({ latitude: null, longitude: null });
  const navigate = useNavigate();

  // Location permission check on mount
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      toastError("Geolocation is not supported in this browser.");
      setLocationAllowed(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationAllowed(true);
        setCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        toastError("Please allow location access to continue.");
        setLocationAllowed(false);
      }
    );
  }, []);

  const getDeviceId = () => {
    let deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem("deviceId", deviceId);
    }
    return deviceId;
  };

  const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) return "android";
    if (/iPad|iPhone|iPod/.test(ua)) return "ios";
    return "desktop";
  };

  const getDeviceToken = () => {
    return uuidv4();
  };

  // STEP 1 → Email check
  const handleEmailCheck = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post("/admin/api/user/login", { email });
      const { Status, user, purchasePlans, Message } = response.data;

      if (Status !== 1) {
        toastError(Message || "Email not found");
        return;
      }

      const userRole = user.roles?.[0]?.name || "";
      let planResponsibilities = [];
      if (Array.isArray(purchasePlans)) {
        purchasePlans.forEach((purchase) => {
          if (Array.isArray(purchase.plan)) {
            purchase.plan.forEach((plan) => {
              if (Array.isArray(plan.PlanResponsibility)) {
                planResponsibilities.push(
                  ...plan.PlanResponsibility.filter((r) => r && r.name).map((r) => r.name)
                );
              }
            });
          }
        });
      }

      localStorage.setItem("email", user.email);
      localStorage.setItem("role", userRole);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("planResponsibilities", JSON.stringify(planResponsibilities));

      if (userRole === "SuperAdmin") {
        setShowPassword(true);
      } else {
        toastInfo("Redirecting to OTP...");
        navigate("/otp");
      }
    } catch (error) {
      // Show backend error if available
      const backendMsg = error.response?.data?.Message;
      toastError(backendMsg || "Something went wrong, please try again");
    }
  };

  // STEP 2 → Password login (with location + device)
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!locationAllowed || !coords.latitude || !coords.longitude) {
      toastError("Location not available. Please allow location access.");
      return;
    }

    const deviceId = getDeviceId();
    const devicetype = getDeviceType();
    const devicetoken = getDeviceToken();

    try {
      const response = await axios.post("/admin/api/user/login", {
        email,
        password,
        latitude: coords.latitude,
        longitude: coords.longitude,
        deviceId,
        devicetype,
        devicetoken,
      });

      const { Status, UserToken, user, defaultCountry, defaultState, defaultCity, purchasePlans, Message } =
        response.data;

      if (Status === 1) {
        const role = user.roles?.[0]?.name || "";

        let planResponsibilities = [];
        if (Array.isArray(purchasePlans)) {
          purchasePlans.forEach((purchase) => {
            if (Array.isArray(purchase.plan)) {
              purchase.plan.forEach((plan) => {
                if (Array.isArray(plan.PlanResponsibility)) {
                  planResponsibilities.push(
                    ...plan.PlanResponsibility.filter((r) => r && r.name).map((r) => r.name)
                  );
                }
              });
            }
          });
        }

        localStorage.setItem("email", user.email);
        localStorage.setItem("role", role);
        localStorage.setItem("token", UserToken);
        localStorage.setItem("defaultCountry", defaultCountry?._id || "");
        localStorage.setItem("defaultState", defaultState?._id || "");
        localStorage.setItem("defaultCity", defaultCity?._id || "");
        localStorage.setItem("auth", "true");
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("planResponsibilities", JSON.stringify(planResponsibilities));

        toastSuccess("Login successful");
        navigate("/dashboard");
      } else {
        toastError(Message || "Invalid password");
      }
    } catch (error) {
      // Show backend error if available
      const backendMsg = error.response?.data?.Message;
      toastError(backendMsg || "Something went wrong, please try again");
    }
  };

  return (
    <div className="">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-center min-vh-100">
              <div className="w-50 d-block bg-white shadow-lg rounded my-5">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="p-5">
                      <h1 className="h5 mb-3 text-center">Welcome Back!</h1>
                      
                      {locationAllowed === null ? (
                        <div className="text-center mb-4">Requesting location access. Please allow to continue…</div>
                      ) : locationAllowed === false ? (
                        <div className="text-danger text-center mb-4">
                          Location access required to continue.
                        </div>
                      ) : !showPassword ? (
                        <form onSubmit={handleEmailCheck}>
                          <div className="form-group">
                            <input
                              type="email"
                              placeholder="Email Address"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="form-control form-control-user"
                              required
                            />
                          </div>
                          <button type="submit" className="btn btn-success btn-block">
                            Login
                          </button>
                        </form>
                      ) : (
                        <form onSubmit={handleLogin}>
                          <div className="form-group">
                            <input
                              type="email"
                              value={email}
                              readOnly
                              className="form-control form-control-user mb-2"
                            />
                          </div>
                          <div className="form-group">
                            <input
                              type="password"
                              placeholder="Password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              className="form-control form-control-user"
                              required
                            />
                          </div>
                          <button type="submit" className="btn btn-success btn-block">
                            Log In
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;